/**
 * ContactDetailPage — W4.8
 *
 * Detail-Ansicht eines Contacts.
 * Hero: Name + Email + Helium-Wallet (Adress-Chip mit Copy) + Monthly-Report-Badge.
 * Shares-Liste mit Edit/Delete pro Share.
 * "Add Share"-Button -> Modal.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from '@tanstack/react-router'
import { Copy, Check, ArrowLeft, Plus } from 'lucide-react'
import { useContactDetail } from './useContactDetail'
import { EditContactModal } from './EditContactModal'
import { DeleteConfirmModal } from './DeleteConfirmModal'
import { AddShareModal } from './AddShareModal'
import { useDeleteContact } from './useContactMutations'
import { useDeleteShare } from './useShareMutations'
import { ContactPaymentsSection } from './ContactPaymentsSection'
import { PaymentFormModal } from '../payments/PaymentFormModal'
import { NotesList } from '../notes/NotesList'
import { TagPicker } from '../tags/TagPicker'
import { useTags } from '../tags/useTags'
import { formatNumber } from '../../components/ui/format'
import { activeBcp47, formatLocalDate } from '../../lib/locale'
import type { Share } from './useContactDetail'
import {
  useHosts,
  useHostSplitRules,
  useHostsSettlements,
  useHotspotNameMap,
} from '../hosts/useHosts'
import type { HostSplitRule, Settlement } from '../hosts/useHosts'
import { SettlementStatusPill } from '../settlements/SettlementsPage'

/** dd/mm/yyyy im aktiven Locale — wie ContactPaymentsSection.formatDate. */
function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  return new Date(iso).toLocaleDateString(activeBcp47(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Issue #136 — Beteiligungen (Shares) mit Enddatum in der Vergangenheit sind
 * "abgelaufen", nicht mehr aktiv. Vergleich rein kalendarisch (Tagesgenauigkeit,
 * UTC-Extraktion wie formatLocalDate) — ein end_date von "heute" gilt noch als
 * aktiv (laeuft erst mit Ablauf des Tages ab).
 */
function isShareExpired(share: Share): boolean {
  if (!share.end_date) return false
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(share.end_date)
  if (!match) return false
  const [, year, month, day] = match
  const end = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (Number.isNaN(end.getTime())) return false
  const now = new Date()
  const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
  return end.getTime() < today.getTime()
}

/**
 * Summiert die geladenen Settlements des verknuepften Hosts in offen
 * (draft/approved) vs. bezahlt (settled) — analog ContactsPage.hostBalances,
 * hier lokal dupliziert (kleine Pure-Funktion, kein eigenes Modul noetig).
 */
function personBalances(
  hostId: number | undefined,
  settlements: Settlement[] | undefined,
): { open: string | null; paid: string | null } {
  if (hostId === undefined) return { open: null, paid: null }
  let openAmount = 0
  let openCurrency: string | undefined
  let paidAmount = 0
  let paidCurrency: string | undefined

  for (const s of settlements ?? []) {
    if (s.host_id !== hostId) continue
    const amount = Number(s.total_amount ?? '0')
    if (!Number.isFinite(amount)) continue
    if (s.status === 'settled') {
      paidAmount += amount
      paidCurrency = s.currency ?? paidCurrency
    } else {
      openAmount += amount
      openCurrency = s.currency ?? openCurrency
    }
  }

  return {
    open: openCurrency ? `${formatNumber(openAmount, 2)} ${openCurrency}` : null,
    paid: paidCurrency ? `${formatNumber(paidAmount, 2)} ${paidCurrency}` : null,
  }
}

// --- Host-Merge (person-detail.html: Host + Contact sind eine "Person") ---
//
// Es gibt (noch) keinen /api/v2/people-Endpunkt (SPEC.md Backlog #4).
// Merge-Schluessel ist E-Mail (case-insensitive) — identisch zum Schluessel,
// den ContactsPage.tsx (die /people-Liste) bereits verwendet. Wallet-Abgleich
// entfaellt bewusst: Contact.helium_wallet und Host.solana_wallet_address
// sind fachlich verschiedene Felder ohne verlaessliche 1:1-Beziehung.
function useMatchedHost(contactEmail: string | null) {
  const { hosts } = useHosts()
  const emailKey = contactEmail?.toLowerCase()
  return hosts?.find((h) => h.email?.toLowerCase() === emailKey)
}

// --- Copy-Chip Komponente ---

function CopyChip({ value }: { value: string }) {
  const { t } = useTranslation('contacts')
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard-API nicht verfügbar
    }
  }

  return (
    <button
      type="button"
      className="copy-chip btn btn-ghost btn-sm"
      onClick={() => {
        void handleCopy()
      }}
      aria-label={copied ? t('detail.copiedAriaLabel') : t('detail.copyAriaLabel')}
      title={value}
      data-testid="wallet-copy-btn"
    >
      <span className="copy-chip-text">
        {value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value}
      </span>
      {copied ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
    </button>
  )
}

// --- Share Row ---

type ShareRowProps = {
  share: Share
  contactId: string
}

/** Beispielwert (HNT-Rewards) fuer die Live-Vorschau — wie im Prototyp (person-detail.html). */
const RULE_EXAMPLE_HNT = 10

function ShareRow({ share, contactId }: ShareRowProps) {
  const { t } = useTranslation('contacts')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const deleteShare = useDeleteShare(contactId)
  const expired = isShareExpired(share)

  const amountLabel =
    share.percentage != null
      ? `${share.percentage}%`
      : share.fixed_amount != null
        ? `${share.fixed_amount} HNT`
        : '—'

  // Regel-Zusammenfassung mit Live-Beispiel (SPEC person-detail.html: "rule
  // summary w/ live example"). Basis ist immer HNT-Rewards (Share-Modell
  // kennt keine GB-Basis, anders als HostSplitRule). Fuer abgelaufene
  // Beteiligungen (Issue #136) keine Live-Vorschau — sie erhalten aktuell
  // keine Ausschuettung mehr, die Vorschau waere irrefuehrend.
  const rulePreview = expired
    ? null
    : share.percentage != null
      ? t('shareRow.previewPercentage', {
          example: RULE_EXAMPLE_HNT,
          result: ((RULE_EXAMPLE_HNT * share.percentage) / 100).toFixed(4),
        })
      : share.fixed_amount != null
        ? t('shareRow.previewFixed', { amount: share.fixed_amount })
        : null

  async function handleDelete() {
    try {
      await deleteShare.mutateAsync(share.id)
      setDeleteOpen(false)
    } catch {
      // Fehler im UI
    }
  }

  return (
    <>
      <div
        className={expired ? 'list-row muted' : 'list-row'}
        role="listitem"
        data-testid={`share-row-${share.id}`}
      >
        <div className="identity-meta">
          <div className="identity-label">{share.hotspot_name}</div>
          <div className="identity-sub">
            <span className="mono">{amountLabel}</span>
            {share.tags && share.tags.length > 0 && (
              <span style={{ marginLeft: 8, display: 'inline-flex', gap: 4 }}>
                {share.tags.map((tag) => (
                  <span key={tag} className="pill">
                    {tag}
                  </span>
                ))}
              </span>
            )}
          </div>
          {expired && (
            <div
              className="muted"
              style={{ fontSize: 12, marginTop: 2 }}
              data-testid={`share-expired-${share.id}`}
            >
              {t('shareRow.expiredLabel', { date: formatLocalDate(share.end_date) })}
            </div>
          )}
          {rulePreview && (
            <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>
              {rulePreview}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('shareRow.editAriaLabel', { hotspotName: share.hotspot_name })}
            onClick={() => setEditOpen(true)}
            data-testid={`share-edit-${share.id}`}
          >
            {t('shareRow.editBtn')}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm is-danger"
            aria-label={t('shareRow.deleteAriaLabel', { hotspotName: share.hotspot_name })}
            onClick={() => setDeleteOpen(true)}
            data-testid={`share-delete-${share.id}`}
          >
            {t('shareRow.deleteBtn')}
          </button>
        </div>
      </div>

      <AddShareModal
        contactId={contactId}
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        share={share}
      />

      <DeleteConfirmModal
        title={t('shareRow.deleteTitle')}
        message={t('shareRow.deleteMessage', { hotspotName: share.hotspot_name })}
        isOpen={deleteOpen}
        isPending={deleteShare.isPending}
        onConfirm={() => {
          void handleDelete()
        }}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  )
}

// --- Host-Regel-Zeile (Provisions-Regel des verknuepften Hosts) ---

function hostRulePreview(
  rule: HostSplitRule,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const basisKey = rule.basis === 'gigabytes_transferred' ? 'hostRule.basisGb' : 'hostRule.basisHnt'
  const basisLabel = t(basisKey)
  if (rule.split_type === 'percentage' && rule.percentage != null) {
    return t('hostRule.summaryPercentage', { percentage: rule.percentage, basis: basisLabel })
  }
  if (rule.split_type === 'fixed_amount' && rule.fixed_amount != null) {
    return t('hostRule.summaryFixed', {
      amount: rule.fixed_amount,
      currency: rule.payout_currency ?? 'HNT',
    })
  }
  return '—'
}

/**
 * Host-verknuepfte Karten (nur sichtbar, wenn ein Host mit derselben E-Mail
 * existiert — echter clientseitiger People-Merge, siehe useMatchedHost).
 * Zeigt die REALEN Provisions-Regeln (mit Basis HNT/GB, anders als das
 * Contact-Share-Modell), Abrechnungshistorie und gehostete Hotspots.
 */
function HostSection({ hostId }: { hostId: number }) {
  const { t } = useTranslation('contacts')
  const { rules, isLoading: rulesLoading } = useHostSplitRules()
  const { settlements, isLoading: settlementsLoading } = useHostsSettlements()
  const hotspotNames = useHotspotNameMap()

  const hostRules = (rules ?? []).filter((r) => r.host_id === hostId)
  const hostSettlements = (settlements ?? [])
    .filter((s) => s.host_id === hostId)
    .sort((a, b) => (b.period_month ?? '').localeCompare(a.period_month ?? ''))
  const hotspotIds = Array.from(
    new Set(hostRules.map((r) => r.hotspot_id).filter((v): v is number => v !== undefined)),
  )

  return (
    <>
      <section className="card card-pad" aria-label={t('hostRule.sectionTitle')}>
        <h2 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>
          {t('hostRule.sectionTitle')}
        </h2>
        {rulesLoading && <div className="skeleton-line" style={{ width: 200, height: 14 }} />}
        {!rulesLoading && hostRules.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--text-subtle)', margin: 0 }}>
            {t('hostRule.empty')}
          </p>
        )}
        {!rulesLoading && hostRules.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {hostRules.map((rule) => (
              <div key={rule.id} data-testid={`host-rule-${rule.id}`}>
                <div className="mono" style={{ fontSize: 14, fontWeight: 600 }}>
                  {hostRulePreview(rule, t)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
                  {hotspotNames.get(String(rule.hotspot_id)) ?? t('hostRule.unknownHotspot')}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="card card-pad" aria-label={t('hostRule.settlementsSectionTitle')}>
        <h2 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>
          {t('hostRule.settlementsSectionTitle')}
        </h2>
        {settlementsLoading && <div className="skeleton-line" style={{ width: 200, height: 14 }} />}
        {!settlementsLoading && hostSettlements.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--text-subtle)', margin: 0 }}>
            {t('hostRule.settlementsEmpty')}
          </p>
        )}
        {!settlementsLoading && hostSettlements.length > 0 && (
          <div className="list" role="list">
            {hostSettlements.map((s) => (
              <div
                key={s.id}
                className="list-row"
                role="listitem"
                data-testid={`host-settlement-${s.id}`}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span style={{ fontSize: 13 }}>{s.period_month}</span>
                <span className="mono" style={{ fontSize: 13 }}>
                  {s.gigabytes_transferred} GB · {s.hnt_rewards} HNT · {s.total_amount} {s.currency}
                </span>
                <SettlementStatusPill status={s.status} id={s.id} />
              </div>
            ))}
          </div>
        )}
      </section>

      {hotspotIds.length > 0 && (
        <section className="card card-pad" aria-label={t('hostRule.hotspotsSectionTitle')}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              {t('hostRule.hotspotsSectionTitle')}
            </h2>
            <span className="pill">{hotspotIds.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {hotspotIds.map((hid) => (
              <Link
                key={hid}
                to="/hotspots/$id"
                params={{ id: String(hid) }}
                className="ellipsis"
                style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}
              >
                {hotspotNames.get(String(hid)) ?? t('hostRule.unknownHotspot')}
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  )
}

// --- Main Component ---

type Props = {
  id: string
}

export function ContactDetailPage({ id }: Props) {
  const { t } = useTranslation('contacts')
  const { t: tShell } = useTranslation('shell')
  const navigate = useNavigate()
  const { contact, isLoading, isError, isNotFound, refetch } = useContactDetail(id)
  const { data: allTags } = useTags()
  // Hook-Reihenfolge: vor jedem early return aufrufen (Rules of Hooks).
  const matchedHost = useMatchedHost(contact?.email ?? null)
  const { settlements } = useHostsSettlements()
  const balances = personBalances(matchedHost?.id, settlements)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [addShareOpen, setAddShareOpen] = useState(false)
  // R6/MP — Einstieg 'Zahlung erfassen' mit vorbelegtem Kontakt.
  const [trackPaymentOpen, setTrackPaymentOpen] = useState(false)
  const deleteContact = useDeleteContact()

  const contactNumericId = contact?.id ?? Number(id)
  // Issue #136 — aktive vs. abgelaufene Beteiligungen (Enddatum in der
  // Vergangenheit) trennen; aktive zuerst, Reihenfolge sonst wie geliefert.
  const activeShares = (contact?.shares ?? []).filter((s) => !isShareExpired(s))
  const expiredShares = (contact?.shares ?? []).filter((s) => isShareExpired(s))
  const assignedTagIds = (allTags ?? [])
    .filter(
      (t) =>
        t.contact_ids?.includes(contactNumericId) ??
        t.contacts?.some((c) => c.id === contactNumericId),
    )
    .map((t) => t.id)

  async function handleDeleteContact() {
    try {
      await deleteContact.mutateAsync(id)
      setDeleteOpen(false)
      void navigate({ to: '/people' })
    } catch {
      // Fehler im UI
    }
  }

  // --- Loading ---
  if (isLoading) {
    return (
      <div aria-busy="true" data-testid="skeleton-contact-detail">
        <div className="skeleton-line" style={{ width: 200, height: 24, marginBottom: 16 }} />
        <div className="skeleton-line" style={{ width: 300, height: 14 }} />
      </div>
    )
  }

  // --- 404 ---
  if (isNotFound) {
    return (
      <div className="empty-state" data-testid="not-found-state">
        <p>{t('detail.notFound')}</p>
        <button className="btn btn-ghost" onClick={() => void navigate({ to: '/people' })}>
          <ArrowLeft size={14} aria-hidden="true" />
          {t('detail.backBtn')}
        </button>
      </div>
    )
  }

  // --- Error ---
  if (isError) {
    return (
      <div className="error-state" role="alert">
        <p>{t('detail.errorLoad')}</p>
        <button className="btn btn-primary" onClick={refetch}>
          {t('common:actions.retry')}
        </button>
      </div>
    )
  }

  if (!contact) return null

  return (
    <>
      <main className="contact-detail-page">
        {/* Breadcrumb — person-detail.html: "Earnings > People > {name}" */}
        <nav
          aria-label={t('detail.breadcrumbAriaLabel')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--text-subtle)',
            marginBottom: 12,
          }}
        >
          <span>{tShell('nav.sections.income')}</span>
          <span aria-hidden="true">›</span>
          <Link to="/people" style={{ color: 'var(--text-subtle)' }}>
            {tShell('nav.items.people')}
          </Link>
          <span aria-hidden="true">›</span>
          <span style={{ color: 'var(--text)' }}>{contact.name}</span>
        </nav>

        {/* Back nav */}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => void navigate({ to: '/people' })}
          style={{ marginBottom: 16 }}
        >
          <ArrowLeft size={14} aria-hidden="true" />
          {t('detail.backBtn')}
        </button>

        {/* Hero */}
        <section
          className="card frost"
          aria-label={t('detail.heroAriaLabel')}
          style={{ marginBottom: 24 }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div className="identity hero-identity">
              <div className="avatar avatar--lg" aria-hidden="true">
                {contact.name
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0].toUpperCase())
                  .join('')}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{contact.name}</h1>
                  {/* Rollen-Chips wie person-detail.html: "Host" wenn ein Host mit
                      derselben E-Mail existiert (clientseitiger People-Merge,
                      identischer Schluessel zur /people-Liste), "Contact" immer. */}
                  {matchedHost && <span className="pill pill-primary">{t('detail.roleHost')}</span>}
                  <span className="pill">{t('detail.roleContact')}</span>
                </div>

                {/* Subtitle-Zeile wie person-detail.html: "email · since date · tags: x, y" */}
                <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
                  {[
                    contact.email,
                    formatDate(contact.created_at) &&
                      t('detail.since', { date: formatDate(contact.created_at) }),
                    contact.tag_list && t('detail.tagsInline', { tags: contact.tag_list }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {contact.monthly_report && (
                    <span
                      className="badge is-success"
                      aria-label={t('detail.monthlyReportAriaLabel')}
                      data-testid="monthly-report-badge"
                    >
                      {t('detail.monthlyReportBadge')}
                    </span>
                  )}
                  {contact.broadcast_enabled === false && (
                    <span className="badge" aria-label={t('detail.broadcastDisabledAriaLabel')}>
                      {t('detail.broadcastDisabledBadge')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Hero actions — read view; Edit/Settlements secondary, Delete
                ghost-danger (person-detail.html), Edit/Delete bleiben separate
                Aktionen (Modal bzw. Confirm), keine Inline-Bearbeitung hier. */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEditOpen(true)}
              >
                {t('detail.editBtn')}
              </button>
              <Link to="/settlements" className="btn btn-secondary btn-sm">
                {t('detail.settlementsBtn')}
              </Link>
              <button
                type="button"
                className="btn btn-ghost btn-sm is-danger"
                onClick={() => setDeleteOpen(true)}
              >
                {t('detail.deleteBtn')}
              </button>
              {/* R6/MP — v1-Paritaet zu app/views/contacts/show.haml:59
                  ('add Payment' mit vorbelegtem Kontakt). */}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setTrackPaymentOpen(true)}
                data-testid="track-payment-btn"
              >
                {t('detail.trackPaymentBtn')}
              </button>
            </div>
          </div>
        </section>

        {/*
          Zweispaltiges Layout wie person-detail.html (.grid2): links Regeln
          (Anteile inkl. Live-Beispiel) + Zahlungshistorie + Notizen, rechts
          Tags + Stammdaten. Bei schmalen Viewports (< ~680px, .grid-2 aus
          utilities.css) stapelt die Spalte einspaltig — kein horizontales
          Scrollen bei 390px.
        */}
        <div className="grid grid-2" style={{ alignItems: 'start', marginBottom: 24 }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
            className="contact-detail-col-main"
          >
            {/* Shares Section (Provisions-Regeln / "commission rule") */}
            <section aria-label={t('detail.sharesAriaLabel')}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12,
                }}
              >
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                  {t('detail.sharesSection', { count: contact.shares?.length ?? 0 })}
                </h2>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setAddShareOpen(true)}
                  data-testid="add-share-btn"
                >
                  <Plus size={14} aria-hidden="true" />
                  {t('detail.addShareBtn')}
                </button>
              </div>

              {(contact.shares?.length ?? 0) === 0 ? (
                <div className="empty-state" data-testid="shares-empty">
                  <p>{t('detail.sharesEmpty')}</p>
                </div>
              ) : (
                <>
                  {activeShares.length > 0 && (
                    <div className="list card" role="list" aria-label={t('detail.sharesAriaLabel')}>
                      {activeShares.map((share) => (
                        <ShareRow key={share.id} share={share} contactId={id} />
                      ))}
                    </div>
                  )}

                  {/* Issue #136 — abgelaufene Beteiligungen (end_date in der
                  Vergangenheit) separat unter eigener Ueberschrift, aktive
                  Beteiligungen bleiben oben. */}
                  {expiredShares.length > 0 && (
                    <div
                      style={{ marginTop: activeShares.length > 0 ? 20 : 0 }}
                      data-testid="expired-shares-section"
                    >
                      <h3
                        className="muted"
                        style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600 }}
                      >
                        {t('detail.pastSharesSection')}
                      </h3>
                      <div
                        className="list card"
                        role="list"
                        aria-label={t('detail.pastSharesSection')}
                      >
                        {expiredShares.map((share) => (
                          <ShareRow key={share.id} share={share} contactId={id} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Payments Section (Zahlungs-/Settlement-Historie) */}
            <ContactPaymentsSection contactId={id} />

            {/* Notes Section */}
            <section aria-label={t('detail.notesSection')}>
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                {t('detail.notesSection')}
              </h2>
              <NotesList originType="Contact" originId={contact.id ?? id} />
            </section>
          </div>

          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
            className="contact-detail-col-side"
          >
            {/* Details-Karte — person-detail.html .kv: Email/Phone/Wallet/Currency/
            Open balance/Total paid. Wallet bevorzugt Contact.helium_wallet,
            faellt sonst auf die Solana-Adresse des verknuepften Hosts zurueck
            (matchedHost) — beide Rollen derselben Person teilen sich eine
            Wallet-Zeile wie im Prototyp. */}
            <section className="card frost" aria-label={t('detail.detailsCardTitle')}>
              <h2 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>
                {t('detail.detailsCardTitle')}
              </h2>
              <dl
                className="kv"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  gap: '6px 12px',
                  fontSize: 13,
                  margin: 0,
                }}
              >
                <dt style={{ color: 'var(--text-subtle)' }}>{t('detail.emailLabel')}</dt>
                <dd style={{ margin: 0 }}>{contact.email ?? t('peoplePage.noValue')}</dd>
                <dt style={{ color: 'var(--text-subtle)' }}>{t('detail.phoneLabel')}</dt>
                <dd style={{ margin: 0 }}>{contact.phone ?? t('peoplePage.noValue')}</dd>
                <dt style={{ color: 'var(--text-subtle)' }}>{t('detail.walletLabel')}</dt>
                <dd style={{ margin: 0 }}>
                  {contact.helium_wallet || matchedHost?.solana_wallet_address ? (
                    <CopyChip
                      value={
                        (contact.helium_wallet || matchedHost?.solana_wallet_address) as string
                      }
                    />
                  ) : (
                    t('peoplePage.noValue')
                  )}
                </dd>
                <dt style={{ color: 'var(--text-subtle)' }}>{t('detail.waehrung')}</dt>
                <dd style={{ margin: 0 }}>
                  {(contact.currency ?? matchedHost?.currency_preference)?.toUpperCase() ??
                    t('peoplePage.noValue')}
                </dd>
                <dt style={{ color: 'var(--text-subtle)' }}>
                  {t('peoplePage.columns.openBalance')}
                </dt>
                <dd style={{ margin: 0 }} className="mono">
                  {balances.open ?? t('peoplePage.noValue')}
                </dd>
                <dt style={{ color: 'var(--text-subtle)' }}>{t('peoplePage.columns.totalPaid')}</dt>
                <dd style={{ margin: 0 }} className="mono">
                  {balances.paid ?? t('peoplePage.noValue')}
                </dd>
              </dl>
            </section>

            {/* Tags — F031 */}
            <section className="card frost" aria-label={t('detail.tagsSection')}>
              <h2 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 600 }}>
                {t('detail.tagsSection')}
              </h2>
              <TagPicker
                targetType="contact"
                targetId={contactNumericId}
                assignedTagIds={assignedTagIds}
              />
            </section>

            {/* Weitere Detail-Felder (nicht im Prototyp-.kv, aber bestehende
            Contact-Funktionalitaet — Adresse/Notizen/Zusatz-Wallets/
            Zahlungsdetails bleiben erhalten, Telefon+Waehrung stehen bereits
            oben in der Details-Karte). */}
            {(contact.address ||
              contact.notes ||
              contact.btc_wallet ||
              contact.eth_wallet ||
              contact.usdt_wallet ||
              contact.usdc_wallet ||
              contact.payment_method ||
              contact.iban ||
              contact.paypal) && (
              <section className="card frost" aria-label={t('detail.detailsSection')}>
                {(contact.address || contact.notes) && (
                  <div style={{ marginBottom: 16 }}>
                    <h2
                      style={{
                        margin: '0 0 8px',
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: 'var(--text-subtle)',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {t('detail.masterdata')}
                    </h2>
                    {contact.address && (
                      <div style={{ fontSize: 14, marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-subtle)' }}>{t('detail.address')}</span>{' '}
                        {contact.address}
                      </div>
                    )}
                    {contact.notes && (
                      <div style={{ fontSize: 14, marginTop: 6, whiteSpace: 'pre-line' }}>
                        <span style={{ color: 'var(--text-subtle)' }}>{t('detail.notizen')}</span>
                        <div
                          style={{
                            marginTop: 2,
                            padding: '6px 10px',
                            background: 'var(--surface-2, rgba(0,0,0,0.04))',
                            borderRadius: 6,
                          }}
                        >
                          {contact.notes}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(contact.btc_wallet ||
                  contact.eth_wallet ||
                  contact.usdt_wallet ||
                  contact.usdc_wallet) && (
                  <div style={{ marginBottom: 16 }}>
                    <h2
                      style={{
                        margin: '0 0 8px',
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: 'var(--text-subtle)',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {t('detail.wallets')}
                    </h2>
                    {contact.btc_wallet && (
                      <div
                        style={{
                          fontSize: 14,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span style={{ color: 'var(--text-subtle)', minWidth: 50 }}>BTC:</span>
                        <CopyChip value={contact.btc_wallet} />
                      </div>
                    )}
                    {contact.eth_wallet && (
                      <div
                        style={{
                          fontSize: 14,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span style={{ color: 'var(--text-subtle)', minWidth: 50 }}>ETH:</span>
                        <CopyChip value={contact.eth_wallet} />
                      </div>
                    )}
                    {contact.usdt_wallet && (
                      <div
                        style={{
                          fontSize: 14,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span style={{ color: 'var(--text-subtle)', minWidth: 50 }}>USDT:</span>
                        <CopyChip value={contact.usdt_wallet} />
                      </div>
                    )}
                    {contact.usdc_wallet && (
                      <div
                        style={{
                          fontSize: 14,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span style={{ color: 'var(--text-subtle)', minWidth: 50 }}>USDC:</span>
                        <CopyChip value={contact.usdc_wallet} />
                      </div>
                    )}
                  </div>
                )}

                {(contact.payment_method || contact.iban || contact.paypal) && (
                  <div>
                    <h2
                      style={{
                        margin: '0 0 8px',
                        fontSize: 13,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        color: 'var(--text-subtle)',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {t('detail.payment')}
                    </h2>
                    {contact.payment_method && (
                      <div style={{ fontSize: 14, marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-subtle)' }}>
                          {t('detail.zahlungsart')}
                        </span>{' '}
                        {contact.payment_method}
                      </div>
                    )}
                    {contact.iban && (
                      <div style={{ fontSize: 14, marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-subtle)' }}>{t('detail.iban')}</span>{' '}
                        {contact.iban}
                      </div>
                    )}
                    {contact.paypal && (
                      <div style={{ fontSize: 14, marginBottom: 4 }}>
                        <span style={{ color: 'var(--text-subtle)' }}>{t('detail.paypal')}</span>{' '}
                        {contact.paypal}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Host-Karten (Provisions-Regeln, Abrechnungen, gehostete Hotspots)
            — nur wenn dieser Kontakt zugleich ein Host ist (Email-Merge). */}
            {matchedHost?.id !== undefined && <HostSection hostId={matchedHost.id} />}
          </div>
        </div>
      </main>

      {/* Edit Contact Modal */}
      {editOpen && (
        <EditContactModal contact={contact} isOpen={editOpen} onClose={() => setEditOpen(false)} />
      )}

      {/* Delete Contact Modal */}
      <DeleteConfirmModal
        title={t('detail.deleteContactTitle')}
        message={t('detail.deleteContactMessage', { name: contact.name })}
        isOpen={deleteOpen}
        isPending={deleteContact.isPending}
        onConfirm={() => {
          void handleDeleteContact()
        }}
        onClose={() => setDeleteOpen(false)}
      />

      {/* Add Share Modal */}
      <AddShareModal contactId={id} isOpen={addShareOpen} onClose={() => setAddShareOpen(false)} />

      {/* R6/MP — Manuelle Zahlung mit vorbelegtem Kontakt erfassen */}
      <PaymentFormModal
        isOpen={trackPaymentOpen}
        mode="create"
        presetContactId={contactNumericId}
        onClose={() => setTrackPaymentOpen(false)}
      />
    </>
  )
}
